/**
 * Designer Onboarding Page
 * Profile creation for interior designers
 */

import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { readRedirectTo } from "@/features/auth/utils/post-auth-redirect";
import { DesignerOnboardingFlow } from "@/features/onboarding/components/designer-onboarding-flow";
import { useLeaveOnceOnboarded } from "@/features/onboarding/hooks/use-leave-once-onboarded";
import { useOnboardingPrefill } from "@/features/onboarding/hooks/use-onboarding-prefill";
import {
	BackToRoleButton,
	RolesLoadRetry,
	useOnboardingExit,
} from "@/features/roles/components/back-to-role";
import { analytics } from "@/services/analytics.service";

export function DesignerOnboardingPage() {
	const navigate = useNavigate();
	const location = useLocation();
	// Set when a route guard diverted the visitor here mid-journey; the
	// page they were heading for outranks this role's landing page.
	const redirectTo = readRedirectTo(location.state);
	const markCompletedHere = useLeaveOnceOnboarded(redirectTo ?? "/dashboard");
	const exit = useOnboardingExit(() =>
		navigate("/onboarding", {
			state: {
				allowSelection: true,
				...(redirectTo ? { redirectTo } : {}),
			},
		}),
	);

	// Only an account adding a role has details to carry over.
	const prefill = useOnboardingPrefill(exit.backTo !== null);

	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-dashboard-warm-350 px-4 py-8">
			<div className="w-full max-w-2xl">
				<DesignerOnboardingFlow
					onExit={exit.onExit}
					exitControl={<RolesLoadRetry exit={exit} />}
					backToRole={
						exit.backTo ? (
							<BackToRoleButton role={exit.backTo} variant="panel" />
						) : undefined
					}
					prefill={prefill}
					onProfileCreated={() => {
						markCompletedHere();
						analytics.updateUserTraits({ designerProfileCompleted: true });
					}}
					onAlreadyExists={() => {
						// A 409 means the server holds a finished profile; say so in the
						// store, or the guard sends the designer straight back here.
						useAuthStore.getState().markProfileComplete();
						navigate(redirectTo ?? "/dashboard");
					}}
					onFinished={() => navigate(redirectTo ?? "/dashboard")}
				/>
			</div>
		</div>
	);
}
